import { api } from '@/shared/api/client'
import { url } from '@/shared/lib/query'
import type {
  CloneRoleRequest, CreateRoleRequest, GrantChangePreviewResponse, PageResponse,
  PermissionModuleResponse, PermissionResponse, RoleResponse, UpdateRoleRequest,
} from '@/shared/api/types'

export interface RoleFilters {
  q: string
  system: '' | 'true' | 'false'
  assignable: '' | 'true' | 'false'
  sort: string
}

export const EMPTY_ROLE_FILTERS: RoleFilters = { q: '', system: '', assignable: '', sort: '' }

export const listRoles = (f: RoleFilters, page = 0, size = 100) =>
  api.get<PageResponse<RoleResponse>>(url('/rbac/roles', {
    q: f.q, system: f.system, assignable: f.assignable, sort: f.sort, page, size,
  }))

export const getRole = (id: string) => api.get<RoleResponse>(`/rbac/roles/${id}`)

/** Flat catalogue — every permission, ungrouped. */
export const listPermissions = () => api.get<PermissionResponse[]>('/rbac/permissions')

/** Grouped by module, with display names and ordering — what the matrix renders. */
export const listPermissionModules = () =>
  api.get<PermissionModuleResponse[]>('/rbac/permissions?grouped=true')

export const createRole = (body: CreateRoleRequest) =>
  api.post<RoleResponse>('/rbac/roles', body)

/**
 * PATCH requires `If-Match` carrying the version integer. A non-numeric value is
 * a 400 server-side, and omitting the header entirely is a 400 too — optimistic
 * locking here is mandatory, not opportunistic.
 */
export const updateRole = (id: string, version: number, body: UpdateRoleRequest) =>
  api.patch<RoleResponse>(`/rbac/roles/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

export const deleteRole = (id: string) => api.del<void>(`/rbac/roles/${id}`)

export const cloneRole = (id: string, body: CloneRoleRequest) =>
  api.post<RoleResponse>(`/rbac/roles/${id}/clone`, body)

/** Full replace, not incremental — send the complete desired set. */
export const replaceGrants = (id: string, permissionIds: string[]) =>
  api.put<RoleResponse>(`/rbac/roles/${id}/permissions`, { permissionIds })

/** Computes the diff and the blast radius without persisting anything. */
export const previewGrants = (id: string, permissionIds: string[]) =>
  api.post<GrantChangePreviewResponse>(`/rbac/roles/${id}/permissions/preview`, { permissionIds })

export const userAuthorities = (userId: string) =>
  api.get<{ userId: string; authorities: string[]; fingerprint: string }>(
    `/rbac/users/${userId}/authorities`,
  )
